import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../providers/AuthProvider';
import { attendanceService, AttendanceOverview, AttendanceStatus } from '../../services/attendanceService';
import { tdGroupService, TDGroup } from '../../services/tdGroupService';
import { attendanceRate, formatSessionDate, StatusChip } from './attendanceUi';

type StudentOption = AttendanceOverview['students'][number];

// Vue promo (enseignant·es et admin) : bilan par étudiant·e, et grille séance par séance dès
// qu'un groupe TD ou un·e étudiant·e est sélectionné·e (toute la promo ferait une grille illisible)
const OverviewTab: React.FC = () => {
  const { getValidToken } = useAuth();
  const [tdGroups, setTdGroups] = useState<TDGroup[]>([]);
  const [allStudents, setAllStudents] = useState<StudentOption[]>([]);
  const [tdGroup, setTdGroup] = useState('');
  const [student, setStudent] = useState<StudentOption | null>(null);
  const [data, setData] = useState<AttendanceOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getValidToken();
        if (!token) return;
        setTdGroups(await tdGroupService.list(token));
      } catch {
        // filtre par groupe indisponible : la vue reste utilisable
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getValidToken();
        if (!token) throw new Error('Session expirée, reconnectez-vous.');
        const overview = await attendanceService.getOverview({ td_group: tdGroup || undefined, student_id: student?.id }, token);
        setData(overview);
        if (!tdGroup && !student) setAllStudents(overview.students);
      } catch (e: any) {
        setError(e.message || "Erreur lors du chargement de l'assiduité");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tdGroup, student]);

  const statusOf = useMemo(() => {
    const map = new Map<string, AttendanceStatus>();
    data?.records.forEach(r => map.set(`${r.session_id}:${r.user_id}`, r.status));
    return map;
  }, [data]);

  const studentOptions = tdGroup ? allStudents.filter(s => s.td_group === tdGroup) : allStudents;
  const showGrid = !!(tdGroup || student) && data && data.sessions.length > 0;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField select size="small" label="Groupe TD" value={tdGroup} sx={{ minWidth: 160 }}
          onChange={(e) => { setTdGroup(e.target.value); setStudent(null); }}>
          <MenuItem value="">Toute la promo</MenuItem>
          {tdGroups.map(g => <MenuItem key={g.code} value={g.code}>{g.code}</MenuItem>)}
        </TextField>
        <Autocomplete
          size="small"
          sx={{ minWidth: 280 }}
          options={studentOptions}
          value={student}
          onChange={(_, value) => setStudent(value)}
          getOptionLabel={(s) => `${s.lastname || ''} ${s.firstname || ''} (${s.td_group || '—'})`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => <TextField {...params} label="Étudiant·e" />}
        />
        {loading && <CircularProgress size={24} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Étudiant·e</TableCell>
                <TableCell>Groupe</TableCell>
                <TableCell align="right">Séances passées</TableCell>
                <TableCell align="right">Présences</TableCell>
                <TableCell align="right">Retards</TableCell>
                <TableCell align="right">Absences</TableCell>
                <TableCell align="right">Justifiées</TableCell>
                <TableCell align="right">Non saisies</TableCell>
                <TableCell align="right">Taux</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.students.map(s => {
                const rate = attendanceRate(s.summary);
                return (
                  <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => setStudent(s)}>
                    <TableCell>{s.lastname} {s.firstname}</TableCell>
                    <TableCell>{s.td_group}</TableCell>
                    <TableCell align="right">{s.summary.past}</TableCell>
                    <TableCell align="right">{s.summary.present}</TableCell>
                    <TableCell align="right">{s.summary.late}</TableCell>
                    <TableCell align="right" sx={{ color: s.summary.absent ? 'error.main' : undefined, fontWeight: s.summary.absent ? 600 : undefined }}>
                      {s.summary.absent}
                    </TableCell>
                    <TableCell align="right">{s.summary.excused}</TableCell>
                    <TableCell align="right">{s.summary.unrecorded}</TableCell>
                    <TableCell align="right">{rate === null ? '—' : `${rate} %`}</TableCell>
                  </TableRow>
                );
              })}
              {data.students.length === 0 && (
                <TableRow><TableCell colSpan={9}>Aucun·e étudiant·e pour ce filtre.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {showGrid && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Détail par séance</Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }}>Étudiant·e</TableCell>
                  {data!.sessions.map(session => (
                    <TableCell key={session.id} align="center" sx={{ whiteSpace: 'nowrap', fontSize: 12 }}
                      title={`${formatSessionDate(session.date)} ${session.start_time || ''} — ${session.td_group || 'Promo'}`}>
                      {session.label || session.session_type}<br />
                      <Typography component="span" variant="caption" color="text.secondary">
                        {session.date.slice(8, 10)}/{session.date.slice(5, 7)}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data!.students.map(s => (
                  <TableRow key={s.id}>
                    <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
                      {s.lastname} {s.firstname}
                    </TableCell>
                    {data!.sessions.map(session => (
                      <TableCell key={session.id} align="center">
                        {session.td_group && session.td_group !== s.td_group
                          ? ''
                          : <StatusChip compact status={statusOf.get(`${session.id}:${s.id}`)} isPast={session.is_past} />}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary">
            P présent·e · R retard · A absent·e · J absence justifiée · ? non saisi · · à venir
          </Typography>
        </>
      )}
    </>
  );
};

export default OverviewTab;
